/**
 * Format observation periods without inventing a calendar day for
 * quarterly (YYYY-Qn) or monthly (YYYY-MM) vintages.
 *
 * @param {unknown} raw
 * @param {string} [locale='es']
 * @returns {string|null}
 */
export function formatObservationPeriod(raw, locale = 'es') {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;

  const yq = /^(\d{4})-Q([1-4])$/i.exec(s);
  if (yq) {
    const year = yq[1];
    const q = yq[2];
    return String(locale).startsWith('en') ? `Q${q} ${year}` : `T${q} ${year}`;
  }

  const ym = /^(\d{4})-(\d{2})$/.exec(s);
  if (ym) {
    const d = new Date(Number(ym[1]), Number(ym[2]) - 1, 1);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
  }

  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
  }

  // Opaque labels already human-readable (keep as-is).
  return s;
}

/**
 * Format a publication timestamp as a calendar day without UTC midnight
 * shifting the day in western timezones (e.g. AR UTC-3).
 *
 * Date-only strings and midnight-UTC instants use the ISO calendar date.
 * Other instants use the local calendar day.
 *
 * @param {unknown} raw
 * @param {string} [locale='es']
 * @returns {string|null}
 */
export function formatPublishedAtDate(raw, locale = 'es') {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;

  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const midnightUtc = /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?(Z|[+-]00:00)$/.test(s);
  const ymd = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (ymd && (dateOnly || midnightUtc || !s.includes('T'))) {
    return formatObservationPeriod(ymd[1], locale);
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return ymd ? formatObservationPeriod(ymd[1], locale) : null;
  }
  return d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Shared period label across a mixed-frequency strip, or null when periods differ.
 * Avoids "período julio 2026" on a landing that also shows daily FX.
 *
 * @param {Array<{ observation_period?: string|null, last_update?: string|null }>} indicators
 * @param {string} [locale='es']
 * @returns {string|null}
 */
export function sharedObservationPeriodLabel(indicators, locale = 'es') {
  if (!Array.isArray(indicators) || indicators.length === 0) return null;
  const labels = indicators
    .map((ind) =>
      formatObservationPeriod(ind?.observation_period || ind?.last_update || null, locale),
    )
    .filter(Boolean);
  if (labels.length === 0) return null;
  const first = labels[0];
  return labels.every((label) => label === first) ? first : null;
}
